import { useCallback, useEffect, useRef } from 'react';
import { getVisitData } from '../../services/api';
import { useOASISFormStore } from '../../store/slices/oasisFormSlice';
import { TASK_DETAIL_QIDS } from '../oasis/taskDetailAnswers';
import type { FormValue } from '../oasis/types';

/**
 * Bring the open visit form in step after Task Details saved a value the form
 * also shows (where care was provided, the HCPCS override, the mileage).
 *
 * The form keeps its own copy of every answer and does not re-read the visit
 * once mounted, so the fresh answers are written straight into its store. They
 * come from the server in the form's own answer format. This is a display
 * refresh only: nothing is saved, and the form sends a Task Details answer
 * only when the user changes it there. `qids` limits the refresh to some of
 * those questions.
 *
 * Resolves `false` when the fresh answers could not be read, so the form still
 * shows its older copy. Once the caller unmounts it never writes to the store,
 * which by then may hold another visit's form.
 */
export function useSyncTaskDetailAnswers(
  visitId: string | null | undefined,
  qids: ReadonlySet<string> = TASK_DETAIL_QIDS
): () => Promise<boolean> {
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const visitIdRef = useRef(visitId);
  visitIdRef.current = visitId;
  const qidsRef = useRef(qids);
  qidsRef.current = qids;

  return useCallback(async () => {
    const requestedFor = visitIdRef.current;
    if (!requestedFor || !mountedRef.current) return true;
    try {
      const { data } = await getVisitData({
        path: { visit_id: requestedFor },
        throwOnError: true,
      });
      // The panel may have moved to another visit while this was in flight.
      if (!mountedRef.current || visitIdRef.current !== requestedFor) {
        return true;
      }
      const answers = (data.visit_data ?? {}) as Record<string, FormValue>;
      const { updateFormField } = useOASISFormStore.getState();
      qidsRef.current.forEach(qid => {
        updateFormField(qid, answers[qid] ?? '');
      });
      return true;
    } catch (err) {
      // The form just shows its older copy until the visit is reopened.
      console.error('Failed to refresh task detail answers in the form', err);
      return false;
    }
  }, []);
}
